import React, { useState } from "react";
import { X, Megaphone, Loader2, Trash2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "../../lib/utils";
import type { Campaign } from "../../context/MarketingContext";

interface CampaignDetailsDrawerProps {
  isOpen: boolean;
  campaign: Campaign | null;
  emailTemplateName?: string;
  smsTemplateName?: string;
  customerGroupName?: string;
  onClose: () => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => Promise<void>;
}

export const CampaignDetailsDrawer: React.FC<CampaignDetailsDrawerProps> = ({
  isOpen,
  campaign,
  emailTemplateName,
  smsTemplateName,
  customerGroupName,
  onClose,
  onEdit,
  onDelete,
}) => {
  const [deleting, setDeleting] = useState(false);
  const hasEmail = Boolean(campaign?.emailTemplateId);
  const hasSms = Boolean(campaign?.smsTemplateId);

  const handleDelete = async () => {
    if (!campaign || !onDelete || deleting) return;
    if (!window.confirm(`Delete campaign “${campaign.name}”? This cannot be undone.`)) return;
    try {
      setDeleting(true);
      await onDelete(campaign.id);
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[999]"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <div
        className={cn(
          "fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl z-[1000] transform transition-transform duration-300 ease-in-out flex flex-col",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {isOpen && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="absolute lg:-left-14 top-4 lg:top-8 right-4 lg:right-auto w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-lg lg:shadow-[-4px_0_12px_rgba(0,0,0,0.1)] hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
          >
            <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
          </motion.button>
        )}

        <div className="flex-1 flex flex-col overflow-y-auto bg-[#F9F9F9]">
          <div className="px-5 py-4 bg-white border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                <Megaphone className="w-5 h-5 text-[#885ABB]" />
              </div>
              <div>
                <h2 className="text-[17px] font-bold text-[#402B69]">
                  {campaign?.name || "Campaign"}
                </h2>
                <p className="text-xs text-gray-500">
                  Details for this marketing campaign
                </p>
              </div>
            </div>
            {campaign && (onEdit || onDelete) && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {onDelete && (
                  <button
                    type="button"
                    onClick={() => void handleDelete()}
                    disabled={deleting}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-700 hover:bg-red-50 rounded-lg text-xs font-semibold disabled:opacity-60"
                  >
                    {deleting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                    Delete
                  </button>
                )}
                {onEdit && (
                  <button
                    type="button"
                    onClick={() => onEdit(campaign.id)}
                    disabled={deleting}
                    className="px-3 py-1.5 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-lg text-xs font-semibold shadow-sm disabled:opacity-60"
                  >
                    Edit Campaign
                  </button>
                )}
              </div>
            )}
          </div>

          {campaign ? (
            <div className="flex-1 p-5 space-y-4">
              {campaign.description ? (
                <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Description
                  </h3>
                  <p className="text-sm text-gray-800 whitespace-pre-line">{campaign.description}</p>
                </div>
              ) : null}

              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Audience
                </h3>
                <p className="text-sm text-gray-800">
                  {customerGroupName || "Unknown group"}
                </p>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Channels
                </h3>
                <div className="space-y-2 text-sm text-gray-800">
                  <p>
                    <span className="font-semibold">Email:</span>{" "}
                    {hasEmail ? emailTemplateName || "Selected" : "Not used"}
                  </p>
                  <p>
                    <span className="font-semibold">SMS:</span>{" "}
                    {hasSms ? smsTemplateName || "Selected" : "Not used"}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Credits Used
                </h3>
                <p className="text-lg font-bold text-[#885ABB]">
                  {campaign.creditsUsed.toFixed(2)}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center px-4">
              <div className="text-center text-gray-400 text-sm">
                No campaign selected.
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

